import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { API, imgUrl } from "../../lib/storeApi";
import ImageUpload from "./ImageUpload";
import { serviceAreas, TV_AREA_SLUGS } from "../../data/serviceAreas";

const EMPTY = { after_url: "", before_url: "", caption: "", category: "tv", town: "", fireplace: false };
const CATS = [["tv", "TV install"], ["audio", "Audio install"]];
export const TOWNS = [["rocky-mount", "Rocky Mount"], ...serviceAreas.map((a) => [a.slug, a.city])];
const townName = (s) => (TOWNS.find(([k]) => k === s) || [])[1] || "";
const MAIN_TOWNS = ["rocky-mount", ...TV_AREA_SLUGS];

const MissingTowns = ({ items, onPick }) => {
  const missing = MAIN_TOWNS.filter((t) => !items.some((it) => it.town === t));
  if (!missing.length) return <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="gallery-towns-complete">Every main town has at least one install photo.</p>;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="gallery-missing-towns">
      <p className="text-sm font-semibold text-amber-800">{missing.length} of {MAIN_TOWNS.length} main towns have no install photos yet</p>
      <p className="text-xs text-amber-700 mt-0.5 mb-2">Their location pages show photos from other towns until you add one. Tap a town to add its photo, or set the town on an existing photo below.</p>
      <div className="flex flex-wrap gap-2">
        {missing.map((t) => <button key={t} type="button" onClick={() => onPick(t)} data-testid={`gallery-missing-${t}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-amber-300 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors"><Plus className="w-3 h-3" /> {townName(t)}</button>)}
      </div>
    </div>
  );
};

export const AdminGallery = ({ token }) => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [msg, setMsg] = useState("");
  const auth = { Authorization: `Bearer ${token}` };

  const load = () => fetch(`${API}/gallery`).then((r) => r.json()).then(setItems);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!f.after_url) return setMsg("An AFTER photo is required.");
    const r = await fetch(`${API}/admin/gallery`, { method: "POST", headers: { ...auth, "Content-Type": "application/json" }, body: JSON.stringify(f) });
    if (!r.ok) return setMsg("Could not save entry.");
    setF(EMPTY); setMsg("Added. It is live on the site now."); load();
  };
  const update = async (it, patch) => {
    const body = { after_url: it.after_url, before_url: it.before_url, caption: it.caption, category: it.category, town: it.town || "", fireplace: !!it.fireplace, ...patch };
    const r = await fetch(`${API}/admin/gallery/${it.id}`, { method: "PUT", headers: { ...auth, "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (r.ok) load();
  };
  const formRef = useRef(null);
  const pickTown = (town) => {
    setF({ ...f, town });
    setMsg(`Adding a photo for ${townName(town)}. Upload the AFTER photo below.`);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const del = async (id) => {
    if (!window.confirm("Delete this gallery entry?")) return;
    await fetch(`${API}/admin/gallery/${id}`, { method: "DELETE", headers: auth });
    load();
  };

  return (
    <div data-testid="admin-gallery" className="space-y-6">
      <MissingTowns items={items} onPick={pickTown} />
      <div className="bg-white rounded-xl border border-slate-200 p-5 scroll-mt-24" ref={formRef} data-testid="gallery-add-form">
        <h2 className="font-bold text-[#0a1f44] mb-3">Add an install</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <ImageUpload label="AFTER photo (required)" value={f.after_url} onChange={(v) => { setF({ ...f, after_url: v }); setMsg(""); }} testid="gallery-after-upload" />
          <ImageUpload label="BEFORE photo (optional)" value={f.before_url} onChange={(v) => setF({ ...f, before_url: v })} testid="gallery-before-upload" />
        </div>
        <div className="mb-3 inline-flex p-1 rounded-full bg-slate-100" data-testid="gallery-category-toggle">
          {CATS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setF({ ...f, category: k })} data-testid={`gallery-category-${k}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold ${f.category === k ? "bg-white text-[#0a1f44] shadow" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        <select value={f.town} onChange={(e) => setF({ ...f, town: e.target.value })} data-testid="gallery-town-select" className="mb-3 ml-3 px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
          <option value="">Town (optional)</option>
          {TOWNS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="mb-3 ml-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600">
          <input type="checkbox" checked={f.fireplace} onChange={(e) => setF({ ...f, fireplace: e.target.checked })} data-testid="gallery-fireplace-check" className="w-4 h-4 accent-[#3b9dff]" /> Fireplace install (shows on the fireplace page)
        </label>
        <input value={f.caption} maxLength={200} onChange={(e) => setF({ ...f, caption: e.target.value })} placeholder='Caption, e.g. 65" Frame TV over fireplace, living room, Wilson'
          data-testid="gallery-caption-input" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
        <div className="mt-4 flex items-center gap-3">
          <button onClick={add} data-testid="gallery-add-btn" className="bg-[#3b9dff] hover:bg-[#2b8ae8] text-white px-5 py-2 rounded-full text-sm font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> Add to gallery</button>
          {msg && <span data-testid="gallery-msg" className="text-sm text-slate-600">{msg}</span>}
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 divide-y" data-testid="admin-gallery-list">
        {items.length === 0 && <p className="p-5 text-sm text-slate-500" data-testid="admin-gallery-empty">No entries yet. The gallery stays hidden on the homepage until you add one.</p>}
        {items.map((it, i) => (
          <div key={it.id} data-testid={`admin-gallery-row-${i}`} className="p-4 flex items-center gap-4">
            {it.before_url && <img src={imgUrl(it.before_url)} alt="" className="w-16 h-12 object-cover rounded" />}
            <img src={imgUrl(it.after_url)} alt="" className="w-16 h-12 object-cover rounded" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0a1f44] truncate">
                <span data-testid={`admin-gallery-cat-${i}`} className={`mr-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${it.category === "audio" ? "bg-orange-100 text-orange-700" : "bg-[#eef4ff] text-[#3b9dff]"}`}>{it.category === "audio" ? "Audio" : "TV"}</span>
                {it.town && <span data-testid={`admin-gallery-town-${i}`} className="mr-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700">{townName(it.town) || it.town}</span>}
                {it.caption || "(no caption)"}
              </p>
              <p className="text-xs text-slate-500">{new Date(it.created_at).toLocaleDateString()}</p>
            </div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
              <input type="checkbox" checked={!!it.fireplace} onChange={(e) => update(it, { fireplace: e.target.checked })} data-testid={`admin-gallery-fireplace-${i}`} className="w-4 h-4 accent-[#3b9dff]" /> Fireplace
            </label>
            <select value={it.town || ""} onChange={(e) => update(it, { town: e.target.value })} data-testid={`admin-gallery-town-select-${i}`} className="px-2 py-1 border border-slate-300 rounded-lg text-xs bg-white">
              <option value="">No town</option>
              {TOWNS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button onClick={() => del(it.id)} data-testid={`admin-gallery-delete-${i}`} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
